import { FullValidationErrorFragment } from '@common/graphql/fragments';
import { gql, TypedDocumentNode } from '@common/hooks';
import { RoleFlatType } from '@clinician-profile/graphql';

export const PAGINATED_STAFFING_REQUEST_TEMPLATES_QUERY = gql`
  query PaginatedStaffingRequestTemplatesList(
    $limit: Int!
    $offset: Int!
    $filters: StaffingRequestTemplateFilterInput
    $orderField: StaffingRequestTemplateOrderField
    $orderDirection: OrderDirection
    $staffingRequestTypes: [StaffingRequestType!]
  ) {
    paginatedStaffingRequestTemplates(
      limit: $limit
      offset: $offset
      filters: $filters
      orderField: $orderField
      orderDirection: $orderDirection
      staffingRequestTypes: $staffingRequestTypes
    ) {
      staffingRequestTemplates {
        id
        name
        externalId
        staffingRequestType
        billingRegularRate
        roleLevel
        updatedAt
        organizationUnit {
          id
          name
          department {
            id
            name
            organizationBusiness {
              id
              name
            }
          }
        }
        role {
          id
          abbreviation
        }
        canArchive {
          value
        }
      }
      totalCount
    }
  }
`;

export type PaginatedStaffingRequestTemplatesResponse = {
  paginatedStaffingRequestTemplates: {
    staffingRequestTemplates: StaffingRequestTemplateType[];
    totalCount: number;
  };
};

export type StaffingRequestTemplateType = {
  id: string;
  name: string;
  externalId: string;
  staffingRequestType: string;
  billingRegularRate: string;
  roleLevel: string;
  updatedAt: string;
  organizationUnit: {
    id: string;
    name: string;
    department: {
      id: string;
      name: string;
      organizationBusiness: {
        id: string;
        name: string;
      };
    };
  };
  role: {
    id: string;
    abbreviation: string;
  };
  canArchive: {
    value: string;
  };
};

export type SaffingRequestTemplateQueryResponseType = {
  clinicalSpecialties: {
    id: string;
    displayName: string;
  }[];
  certificationList: {
    id: string;
    name: string;
    credential: string;
  }[];
  yearsOfExperience: {
    enumValues: {
      name: string;
      description: string;
    }[];
  };
  roleLevels: {
    enumValues: {
      name: string;
      description: string;
    }[];
  };
  technologies: {
    name: string;
    id: string;
  }[];
  roles: RoleFlatType[];
  organizationUnits: {
    id: string;
    name: string;
    costCenter: string;
  }[];
  organizationBusinesses: {
    id: string;
    name: string;
  }[];
  worksBillingConfigs: {
    id: string;
    name: string;
  }[];
  worksListTemplates: {
    id: string;
    name: string;
  }[];
  staffingRequestTemplates: {
    id: string;
    name: string;
  }[];
};

export const STAFFING_REQUEST_TEMPLATE_QUERY: TypedDocumentNode<SaffingRequestTemplateQueryResponseType> = gql`
  query StaffingRequestTemplateQuery {
    clinicalSpecialties {
      id
      displayName
    }
    certificationList {
      id
      name
      credential
    }
    technologies {
      name
      id
    }
    roles {
      id
      name
      abbreviation
    }
    organizationUnits {
      id
      name
      costCenter
    }
    organizationBusinesses {
      id
      name
    }
    worksBillingConfigs {
      id
      name
    }
    worksListTemplates {
      id
      name
    }
    staffingRequestTemplates {
      id
      name
    }
  }
`;

export const CREATE_STAFFING_REQUEST_TEMPLATE_MUTATION = gql`
  mutation CreateStaffingRequestTemplateMutation(
    $input: CreateStaffingRequestTemplateMutationInput!
  ) {
    createStaffingRequestTemplate(input: $input) {
      staffingRequestTemplate {
        id
        name
      }
      errors {
        ...FullValidationErrorFragment
      }
    }
  }
  ${FullValidationErrorFragment}
`;

export const UPDATE_STAFFING_REQUEST_TEMPLATE_MUTATION = gql`
  mutation UpdateStaffingRequestTemplateMutation(
    $staffingRequestTemplateId: ID!
    $input: UpdateStaffingRequestTemplateMutationInput!
  ) {
    updateStaffingRequestTemplate(
      staffingRequestTemplateId: $staffingRequestTemplateId
      input: $input
    ) {
      staffingRequestTemplate {
        id
        name
      }
      errors {
        ...FullValidationErrorFragment
      }
    }
  }
  ${FullValidationErrorFragment}
`;

export const GET_STAFFING_REQUEST_TEMPLATE_QUERY = gql`
  query StaffingRequestTemplateQuery($id: ID!) {
    staffingRequestTemplate(id: $id) {
      id
      name
      description
      externalId
      roleLevel
      worksBillingConfig {
        id
        name
      }
      worksListTemplate {
        id
        name
      }
      organization {
        id
        name
      }
      organizationUnit {
        id
        name
        costCenter
        department {
          id
          organizationBusiness {
            id
            name
          }
        }
      }
      role {
        id
        name
        abbreviation
      }
      yearsOfExperience
      billingRegularRate
      staffingRequestType
      staffingRequestTemplateCertifications {
        id
        certification {
          id
          name
          credential
        }
        necessityLevel
      }
      staffingRequestTemplateTechnologies {
        id
        technology {
          id
          name
        }
      }
      staffingRequestTemplateSpecialties {
        id
        necessityLevel
        clinicalSpecialty {
          id
          displayName
          fullName
          specialty
        }
      }
    }
  }
`;
